import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Search,
  Clock,
  X,
  ChevronDown,
  Calendar,
  AlertTriangle
} from 'lucide-react';

import tenant from '../../config/tenant';
import EmployeeSearch from '../../components/common/EmployeeSearch';
import {
  searchEmployee,
  fetchAttendanceForDate,
  submitAdjustment,
  setAttendanceDate,
  setAdjustmentType,
  setAdjustedInTime,
  setAdjustedOutTime,
  setReason,
  setFile,
  setSubmitError,
  clearSearchError,
  clearSubmitError,
  resetForm,
  selectEmployee,
  selectCurrentAttendance,
  selectIsSearching,
  selectSearchError,
  selectAttendanceDate,
  selectAdjustmentType,
  selectAdjustedInTime,
  selectAdjustedOutTime,
  selectReason,
  selectFile,
  selectSubmitting,
  selectSubmitError,
  selectSubmitSuccess,
} from '../../store/slices/attendanceadjustmentSlice';
import {
  fetchAllEmployees,
  fetchApprovedLeaves,
  selectApprovedLeaves,
} from '../../store/slices/employeeSlice';

const ADJUSTMENT_TYPES = [
  'Incorrect IN Time',
  'Incorrect OUT Time',
  'Incorrect IN and OUT Times',
  'Missing IN Time',
  'Missing OUT Time',
  'Missing Full Day',
];

const MAX_REASON_CHARS = 500;

// ─── HELPER FUNCTIONS ────────────────────────────────────────────────────

/**
 * Format date for display (YYYY/MM/DD)
 */
const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.includes('/')) return dateStr;
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[0]}/${parts[1]}/${parts[2]}`;
  }
  return dateStr;
};

/**
 * Convert YYYY/MM/DD back to YYYY-MM-DD
 */
const toDateInputFormat = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.includes('/')) {
    const [y, m, d] = dateStr.split('/');
    return `${y}-${m}-${d}`;
  }
  return dateStr;
};

// ─── DATE PICKER COMPONENT ─────────────────────────────────────────────

/**
 * Date Picker with YYYY/MM/DD Format
 */
function DatePickerWithFormat({ value, onChange, label, required = false, disabled = false, placeholder = 'YYYY/MM/DD' }) {
  const dateInputRef = useRef(null);
  const [displayValue, setDisplayValue] = useState('');

  useEffect(() => {
    setDisplayValue(formatDateForDisplay(value));
  }, [value]);

  const handleTextChange = (e) => {
    let input = e.target.value.replace(/\//g, '');
    let formatted = '';

    if (input.length > 0) {
      if (input.length <= 4) {
        formatted = input;
      } else if (input.length <= 6) {
        formatted = `${input.slice(0, 4)}/${input.slice(4)}`;
      } else {
        formatted = `${input.slice(0, 4)}/${input.slice(4, 6)}/${input.slice(6, 8)}`;
      }
    }

    setDisplayValue(formatted);

    const parts = formatted.split('/');
    if (parts.length === 3 && parts[0].length === 4 && parts[1].length === 2 && parts[2].length === 2) {
      onChange(`${parts[0]}-${parts[1]}-${parts[2]}`);
    }
  };

  const openCalendar = () => {
    if (!disabled && dateInputRef.current) {
      dateInputRef.current.showPicker?.();
    }
  };

  const handleDatePickerChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-gray-700">
        {label} {required && '*'}
      </label>
      <div className="relative">
        <input
          ref={dateInputRef}
          type="date"
          className="absolute opacity-0 w-0 h-0 pointer-events-none"
          value={value || ''}
          onChange={handleDatePickerChange}
          disabled={disabled}
        />
        <div className="relative">
          <input
            type="text"
            className={`w-full rounded-md border border-gray-200 px-3 py-2 text-xs text-gray-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 pr-10 ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'}`}
            value={displayValue}
            onChange={handleTextChange}
            onClick={openCalendar}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={10}
            readOnly={disabled}
          />
          <button
            type="button"
            onClick={openCalendar}
            className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${disabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600'
              }`}
            disabled={disabled}
          >
            <Calendar size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TYPE-FIRST 24H TIME FIELD (00:00 – 23:59, no AM/PM) ────────────────
// Value in and out is always "HH:mm" (24-hour), same as before.
const TimeField24 = ({
  value,
  onChange,
  label,
  required = false,
  disabled = false,
  hasError = false,
}) => {
  const parsed = useMemo(() => {
    if (!value) return { h: '', m: '' };
    const [H, M] = String(value).split(':').map((x) => parseInt(x, 10) || 0);
    return { h: String(H).padStart(2, '0'), m: String(M).padStart(2, '0') };
  }, [value]);

  const [hourText, setHourText] = useState(parsed.h);
  const [minText, setMinText] = useState(parsed.m);

  useEffect(() => { setHourText(parsed.h); }, [parsed.h]);
  useEffect(() => { setMinText(parsed.m); }, [parsed.m]);

  // Commit only when both segments are filled; never wipe mid-typing.
  const commit = (h, m) => {
    if (h === '' && m === '') { onChange(''); return; }
    if (h === '' || m === '') return;
    const H = parseInt(h, 10);
    if (Number.isNaN(H)) return;
    const HH = Math.min(23, Math.max(0, H));
    const MM = Math.min(59, Math.max(0, parseInt(m, 10) || 0));
    onChange(`${String(HH).padStart(2, '0')}:${String(MM).padStart(2, '0')}`);
  };

  const handleHourChange = (e) => {
    let raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    if (raw.length === 2 && parseInt(raw, 10) > 23) raw = '23';
    setHourText(raw);
    if (minText !== '') commit(raw, minText);
  };

  const handleMinChange = (e) => {
    let raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    if (raw.length === 2 && parseInt(raw, 10) > 59) raw = '59';
    setMinText(raw);
    if (raw.length === 2) commit(hourText, raw);
  };

  const step = (which, dir) => {
    if (which === 'h') {
      const cur = parseInt(hourText, 10) || 0;
      const next = (cur + dir + 24) % 24;
      const s = String(next).padStart(2, '0');
      setHourText(s);
      commit(s, minText || '00');
    } else {
      const cur = parseInt(minText, 10) || 0;
      const next = (cur + dir * 5 + 60) % 60;
      const s = String(next).padStart(2, '0');
      setMinText(s);
      commit(hourText || '00', s);
    }
  };

  const handleKeyDown = (which) => (e) => {
    if (e.key === 'ArrowUp') { e.preventDefault(); step(which, +1); }
    if (e.key === 'ArrowDown') { e.preventDefault(); step(which, -1); }
  };

  // Pad a single digit on blur ("8" -> "08") and clear when both are empty.
  const handleBlur = (which) => () => {
    if (hourText === '' && minText === '') { onChange(''); return; }
    if (which === 'h' && hourText.length === 1) {
      const s = hourText.padStart(2, '0');
      setHourText(s);
      if (minText !== '') commit(s, minText);
    }
    if (which === 'm' && minText.length === 1) {
      const s = minText.padStart(2, '0');
      setMinText(s);
      if (hourText !== '') commit(hourText, s);
    }
  };

  const borderCls = hasError
    ? 'border-red-400 focus-within:border-red-500'
    : 'border-gray-200 focus-within:border-primary-500';

  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-gray-700">
        {label} {required && '*'}
      </label>
      <div
        className={`inline-flex items-center h-[38px] px-1 bg-white border rounded-md text-xs text-gray-700 overflow-hidden ${borderCls} ${disabled ? 'bg-gray-50 text-gray-400' : ''
          }`}
      >
        <input
          type="text"
          inputMode="numeric"
          value={hourText}
          onChange={handleHourChange}
          onKeyDown={handleKeyDown('h')}
          onFocus={(e) => e.target.select()}
          onBlur={handleBlur('h')}
          placeholder="HH"
          disabled={disabled}
          className="w-8 text-center outline-none bg-transparent"
          aria-label="Hour (00-23)"
        />
        <span className="text-gray-400 select-none">:</span>
        <input
          type="text"
          inputMode="numeric"
          value={minText}
          onChange={handleMinChange}
          onKeyDown={handleKeyDown('m')}
          onFocus={(e) => e.target.select()}
          onBlur={handleBlur('m')}
          placeholder="mm"
          disabled={disabled}
          className="w-8 text-center outline-none bg-transparent"
          aria-label="Minute"
        />
        <span className="ml-1 pr-1.5 text-[10px] font-semibold text-gray-400 select-none">24h</span>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────

export default function AttendanceAdjustments() {
  const dispatch = useDispatch();

  // Local state
  const [isDragging, setIsDragging] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Redux state
  const employee = useSelector(selectEmployee);
  const currentAttendance = useSelector(selectCurrentAttendance);
  const isSearching = useSelector(selectIsSearching);
  const searchError = useSelector(selectSearchError);
  const attendanceDate = useSelector(selectAttendanceDate);
  const adjustmentType = useSelector(selectAdjustmentType);
  const adjustedInTime = useSelector(selectAdjustedInTime);
  const adjustedOutTime = useSelector(selectAdjustedOutTime);
  const reason = useSelector(selectReason);
  const file = useSelector(selectFile);
  const submitting = useSelector(selectSubmitting);
  const submitError = useSelector(selectSubmitError);
  const submitSuccess = useSelector(selectSubmitSuccess);

  // Load employees from Redux
  const { employees: reduxEmployees, loading: employeesLoading } = useSelector(
    (s) => s.employees || { employees: [], loading: false }
  );

  const [employees, setEmployees] = useState([]);

  // ── Approved leave awareness ─────────────────────────────────────
  const approvedLeaves = useSelector(selectApprovedLeaves);

  const toYMD = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();

    const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (iso) return `${iso[1]}-${String(iso[2]).padStart(2, '0')}-${String(iso[3]).padStart(2, '0')}`;

    const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (slash) return `${slash[3]}-${String(slash[1]).padStart(2, '0')}-${String(slash[2]).padStart(2, '0')}`;

    const d = new Date(s);
    if (isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const leaveOnDate = useMemo(() => {
    const sno = selectedEmployee?.serviceNo || employee?.serviceNo;
    if (!sno || !attendanceDate) return null;
    const sn = String(sno).trim();

    return approvedLeaves.find((l) => {
      if (String(l.HLF_STATUS || '').toUpperCase() !== 'APPROVED') return false;
      if (String(l.HLF_EMP_SERVICE_NO || '').trim() !== sn) return false;

      const from = toYMD(l.HLF_FROM_DATE);
      const to = toYMD(l.HLF_TO_DATE);
      if (!from || !to) return false;

      return attendanceDate >= from && attendanceDate <= to;
    }) || null;
  }, [approvedLeaves, selectedEmployee, employee, attendanceDate]);

  const isOnLeave = !!leaveOnDate;

  // Normalize employees when Redux employees change
  useEffect(() => {
    if (reduxEmployees && Array.isArray(reduxEmployees) && reduxEmployees.length > 0) {
      const normalizedEmployees = reduxEmployees.map(emp => ({
        employeeId: String(emp.HED_EMPLOYEE_ID),
        serviceNo: emp.HED_SERVICE_NO || '',
        name: emp.HED_REPORT_NAME || `${emp.HED_FIRST_NAME || ''} ${emp.HED_LAST_NAME || ''}`.trim() || `Employee ${emp.HED_SERVICE_NO}`,
        department: emp.HDD_DEPARTMENT_NAME || '',
        location: emp.HED_LOCATION_ID || '',
        firstName: emp.HED_FIRST_NAME || '',
        lastName: emp.HED_LAST_NAME || '',
        reportName: emp.HED_REPORT_NAME || '',
      })).filter(emp => emp.employeeId);

      setEmployees(normalizedEmployees);
    }
  }, [reduxEmployees]);

  // Load employees on mount
  useEffect(() => {
    dispatch(fetchAllEmployees());
    dispatch(fetchApprovedLeaves());
  }, [dispatch]);

  // Determine which time fields are needed based on adjustment type
  const needsInTime = [
    'Incorrect IN Time',
    'Incorrect IN and OUT Times',
    'Missing IN Time',
    'Missing Full Day'
  ].includes(adjustmentType);

  const needsOutTime = [
    'Incorrect OUT Time',
    'Incorrect IN and OUT Times',
    'Missing OUT Time',
    'Missing Full Day'
  ].includes(adjustmentType);

  const showCurrentInTime = [
    'Incorrect IN Time',
    'Incorrect IN and OUT Times'
  ].includes(adjustmentType);

  const showCurrentOutTime = [
    'Incorrect OUT Time',
    'Incorrect IN and OUT Times'
  ].includes(adjustmentType);

  // Handle employee selection from dropdown
  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);

    if (employee) {
      if (!attendanceDate) {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        dispatch(setAttendanceDate(`${yyyy}-${mm}-${dd}`));
      }

      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');

      dispatch(searchEmployee({
        searchValue: employee.serviceNo,
        year: year,
        month: month,
      }));
    } else {
      dispatch(resetForm());
    }
  };

  // Fetch attendance when date changes
  useEffect(() => {
    if (employee.serviceNo && attendanceDate) {
      dispatch(fetchAttendanceForDate({
        sno: employee.serviceNo,
        attendanceDate: attendanceDate,
      }));
    }
  }, [attendanceDate, employee.serviceNo, dispatch]);

  // Handle successful submission
  useEffect(() => {
    if (submitSuccess) {
      setTimeout(() => {
        dispatch(resetForm());
        setSelectedEmployee(null);
      }, 2000);
    }
  }, [submitSuccess, dispatch]);

  const handleFile = (selected) => {
    if (!selected) return;
    const maxBytes = (tenant?.rules?.maxUploadSizeMB || 5) * 1024 * 1024;
    if (selected.size > maxBytes) {
      alert(`File is larger than ${tenant?.rules?.maxUploadSizeMB || 5} MB.`);
      return;
    }
    dispatch(setFile(selected));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const getAdjTypeCode = (type) => {
    const mapping = {
      'Incorrect IN Time': 'II',
      'Incorrect OUT Time': 'IO',
      'Incorrect IN and OUT Times': 'IB',
      'Missing IN Time': 'MI',
      'Missing OUT Time': 'MO',
      'Missing Full Day': 'MF',
    };
    return mapping[type] || 'L';
  };

  const handleSubmit = () => {
    if (isOnLeave) {
      dispatch(setSubmitError('This employee is on approved leave on this date. Adjustment is not allowed.'));
      return;
    }
    if (!employee.employeeId) {
      dispatch(setSubmitError('Employee ID could not be resolved. Please re-select the employee.'));
      return;
    }

    if (needsInTime && !adjustedInTime) {
      dispatch(setSubmitError('Please enter the adjusted IN time.'));
      return;
    }

    if (needsOutTime && !adjustedOutTime) {
      dispatch(setSubmitError('Please enter the adjusted OUT time.'));
      return;
    }

    const params = {
      employeeId: employee.employeeId,
      attendanceDate: attendanceDate,
      adjType: getAdjTypeCode(adjustmentType),
      adjReason: reason.trim(),
      adjStatus: 'P',
      userId: employee.employeeId,
    };

    if (needsInTime && adjustedInTime) {
      params.originalInTime = currentAttendance?.inTime !== '—' ? currentAttendance?.inTime : '';
      params.inTime = `${attendanceDate} ${adjustedInTime}:00.000`;
    }

    if (needsOutTime && adjustedOutTime) {
      params.originalOutTime = currentAttendance?.outTime !== '—' ? currentAttendance?.outTime : '';
      params.outTime = `${attendanceDate} ${adjustedOutTime}:00.000`;
    }

    dispatch(submitAdjustment(params));
  };

  const handleCancel = () => {
    dispatch(resetForm());
    setSelectedEmployee(null);
  };

  const canSubmit =
    !isOnLeave &&
    employee.serviceNo &&
    attendanceDate &&
    reason.trim().length > 0 &&
    (!needsInTime || adjustedInTime) &&
    (!needsOutTime || adjustedOutTime);

  return (
    <div className="min-h-full bg-surface-50 flex flex-col text-sm">

      {/* Header */}
      <div className="mb-3 flex-shrink-0">
        <h1 className="text-2xl font-bold text-gray-900">
          Time Adjustments
        </h1>

        <p className="mt-1 text-sm text-gray-500">
          Correct employee attendance records and submit adjustment requests for approval.
        </p>
      </div>

      {/* Error Message */}
      {submitError && (
        <div className="mb-2 flex flex-shrink-0 items-center justify-between rounded-lg border border-red-200 bg-red-50 px-3 py-2">
          <p className="text-xs font-medium text-red-700">
            ❌ {submitError}
          </p>

          <button
            onClick={() => dispatch(clearSubmitError())}
            className="text-red-700 hover:text-red-900"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Success Message */}
      {submitSuccess && (
        <div className="mb-2 flex flex-shrink-0 items-center justify-between rounded-lg border border-green-200 bg-green-50 px-3 py-2">
          <p className="text-xs font-medium text-green-700">
            Adjustment submitted successfully!
          </p>
        </div>
      )}

      {/* Search Error */}
      {searchError && (
        <div className="mb-2 flex flex-shrink-0 items-center justify-between rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2">
          <p className="text-xs font-medium text-yellow-700">
            {searchError}
          </p>

          <button
            onClick={() => dispatch(clearSearchError())}
            className="text-yellow-700 hover:text-yellow-900"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 min-h-0 flex flex-col gap-3 overflow-y-auto lg:overflow-hidden">

        {/* TOP ROW */}
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 flex-shrink-0">

          {/* Employee Information */}
          <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">

            <h2 className="text-sm font-semibold text-gray-900">
              Employee Information
            </h2>

            {/* Employee Search */}
            <div className="mt-3">
              <EmployeeSearch
                value={selectedEmployee}
                onSelect={handleEmployeeSelect}
                employees={employees}
                loading={employeesLoading || isSearching}
                showAllOption={false}
              />
            </div>

            {/* Employee Details */}
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Service No">
                <input
                  type="text"
                  value={employee.serviceNo}
                  readOnly
                  className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-800"
                />
              </Field>

              <Field label="Employee Name">
                <input
                  type="text"
                  value={employee.name}
                  readOnly
                  className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-800"
                />
              </Field>

              <Field label="Department">
                <input
                  type="text"
                  value={employee.department || '—'}
                  readOnly
                  className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-800"
                />
              </Field>

              <Field label="Location">
                <input
                  type="text"
                  value={employee.location || '—'}
                  readOnly
                  className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-800"
                />
              </Field>

              <Field label="Designation">
                <input
                  type="text"
                  value={employee.designation || '—'}
                  readOnly
                  className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-800"
                />
              </Field>
            </div>


          </section>

          {/* Current Attendance */}
          <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900">Current Attendance</h2>

            <div className="mt-3">
              <DatePickerWithFormat
                label="Attendance Date"
                value={attendanceDate}
                onChange={(date) => dispatch(setAttendanceDate(date))}
                required={true}
                placeholder="YYYY/MM/DD"
              />
            </div>

            {/* ─── approved-leave banner ─── */}
            {isOnLeave && (
              <div className="mt-3 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3">
                <div className="flex-shrink-0 mt-0.5">
                  <AlertTriangle size={16} className="text-amber-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-amber-900">
                    Employee is on approved leave on this date
                  </p>
                  <div className="mt-1 space-y-0.5 text-[11px] text-amber-800">
                    <p>
                      <span className="font-medium">From–To:</span>{' '}
                      {leaveOnDate?.HLF_FROM_DATE?.split(' ')[0] || '—'} →{' '}
                      {leaveOnDate?.HLF_TO_DATE?.split(' ')[0] || '—'}
                    </p>
                    {String(leaveOnDate?.HLF_HALF_DAY || '').toUpperCase() === 'Y' && (
                      <p>
                        <span className="font-medium">Half Day:</span>{' '}
                        {leaveOnDate?.HLF_HALF_DAY_TYPE === 'FIRST_HALF' ? 'First Half'
                          : leaveOnDate?.HLF_HALF_DAY_TYPE === 'SECOND_HALF' ? 'Second Half'
                            : 'Yes'}
                      </p>
                    )}
                    {leaveOnDate?.HLF_REASON && (
                      <p className="truncate">
                        <span className="font-medium">Reason:</span> {leaveOnDate.HLF_REASON}
                      </p>
                    )}
                  </div>
                  <p className="mt-2 text-[11px] font-medium text-amber-900">
                    Attendance adjustment is disabled for leave days.
                  </p>
                </div>
              </div>
            )}

            {currentAttendance ? (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-5 text-center">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">In Time</p>
                  <p className="mt-1 text-xl font-semibold text-gray-900">
                    {currentAttendance.inTime || '—'}
                  </p>
                </div>
                <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-5 text-center">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">Out Time</p>
                  <p className="mt-1 text-xl font-semibold text-gray-900">
                    {currentAttendance.outTime || '—'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-3 flex min-h-[105px] items-center justify-center rounded-md border border-dashed border-gray-200 bg-surface-50 px-3 text-center text-xs text-gray-400">
                {employee.serviceNo
                  ? 'No attendance record found for the selected date.'
                  : 'Search for an employee to load their attendance record.'}
              </div>
            )}
          </section>

        </div>

        {/* ADJUSTMENT DETAILS */}
        <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm lg:flex-1 lg:min-h-0 lg:overflow-y-auto">

          {/* Header */}
          <h2 className="text-sm font-semibold text-gray-900">
            Adjustment Details
          </h2>

          {/* Adjustment Type */}
          <div className="mt-3">

            <Field label="Adjustment Type">

              <div className="relative">

                <select
                  value={adjustmentType}
                  onChange={(e) => dispatch(setAdjustmentType(e.target.value))}
                  disabled={isOnLeave}
                  className="w-full appearance-none rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
                >
                  {ADJUSTMENT_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>

                <ChevronDown
                  size={14}
                  className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                />

              </div>

            </Field>

          </div>

          {/* Time Adjustments */}
          {(needsInTime || needsOutTime) && (
            <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">

              {/* IN TIME */}
              {needsInTime && (
                <div className="rounded-md border border-gray-100 bg-surface-50 p-3">

                  <div className="grid grid-cols-[140px_1fr] gap-3 items-start">

                    {/* Current IN Time */}
                    {showCurrentInTime && (
                      <Field label="Current IN Time">
                        <div className="flex h-[38px] items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 text-xs text-gray-500">
                          {currentAttendance?.inTime || 'No Record'}
                          <Clock size={12} className="text-gray-300" />
                        </div>
                      </Field>
                    )}

                    {/* New / Adjusted IN Time */}
                    <TimeField24
                      value={adjustedInTime}
                      onChange={(value) => dispatch(setAdjustedInTime(value))}
                      label={showCurrentInTime ? 'Adjusted IN Time' : 'New IN Time'}
                      required={needsInTime}
                      disabled={isOnLeave}
                    />

                  </div>

                </div>
              )}

              {/* OUT TIME */}
              {needsOutTime && (
                <div className="rounded-md border border-gray-100 bg-surface-50 p-3">

                  <div className="grid grid-cols-[140px_1fr] gap-3 items-start">

                    {/* Current OUT Time */}
                    {showCurrentOutTime && (
                      <Field label="Current OUT Time">
                        <div className="flex h-[38px] items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 text-xs text-gray-500">
                          {currentAttendance?.outTime || 'No Record'}
                          <Clock size={12} className="text-gray-300" />
                        </div>
                      </Field>
                    )}

                    {/* New / Adjusted OUT Time */}
                    <TimeField24
                      value={adjustedOutTime}
                      onChange={(value) => dispatch(setAdjustedOutTime(value))}
                      label={showCurrentOutTime ? 'Adjusted OUT Time' : 'New OUT Time'}
                      required={needsOutTime}
                      disabled={isOnLeave}
                    />

                  </div>

                </div>
              )}

            </div>
          )}


          {/* Reason */}
          <div className="mt-3">

            <Field label="Reason for Adjustment">

              <textarea
                value={reason}
                maxLength={MAX_REASON_CHARS}
                onChange={(e) => dispatch(setReason(e.target.value))}
                disabled={isOnLeave}
                rows={4}
                placeholder={
                  isOnLeave
                    ? 'Adjustment is disabled for approved leave days.'
                    : 'Explain why this record needs to be corrected…'
                }
                className="w-full resize-none rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
              />

              <p className="text-right text-[10px] text-gray-400">
                {reason.length} / {MAX_REASON_CHARS} characters
              </p>

            </Field>

          </div>

          {/* Buttons */}
          <div className="mt-3 flex justify-end gap-2 border-t border-gray-100 pt-3">

            <button
              onClick={handleCancel}
              className="rounded-md border border-gray-200 bg-white px-5 py-2 text-xs font-medium text-gray-700 hover:bg-surface-50"
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              title={isOnLeave ? 'Cannot adjust attendance on an approved leave day' : undefined}
              className="rounded-md bg-primary-600 px-5 py-2 text-xs font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>

          </div>

        </section>

      </div>

    </div>
  );
}

// ─── HELPER COMPONENTS ────────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-gray-700">{label}</span>
      {children}
    </label>
  );
}